import React, { useCallback, useMemo, useState } from 'react';
import { HeaderMetaConfigContext } from './HeaderMetaConfigContext';
import type { HeaderMetaConfigState } from './HeaderMetaConfigContext';

type HeaderMetaConfigProviderProps = {
  children: React.ReactNode;
};

type HeaderMetaInputValues = Pick<HeaderMetaConfigState, 'name' | 'description'>;

const defaultHeaderMetaInputValues: HeaderMetaInputValues = { name: '', description: '' };

export function HeaderMetaConfigProvider(props: HeaderMetaConfigProviderProps) {
  const { children } = props;
  const [headerMetaInputValues, setHeaderMetaInputValues] = useState<HeaderMetaInputValues>(
    defaultHeaderMetaInputValues,
  );

  const setInputValues = useCallback((inputValueUpdates: Partial<HeaderMetaInputValues>) => {
    setHeaderMetaInputValues((currentInputValues) => {
      const updatedInputValues = { ...currentInputValues, ...inputValueUpdates };
      const hasInputValueChanged =
        updatedInputValues.name !== currentInputValues.name ||
        updatedInputValues.description !== currentInputValues.description;

      if (!hasInputValueChanged) {
        return currentInputValues;
      }

      return updatedInputValues;
    });
  }, []);

  const contextValue: HeaderMetaConfigState = useMemo(() => {
    const headerMetaConfigValue = {
      name: headerMetaInputValues.name,
      description: headerMetaInputValues.description,
      setInputValues,
    };

    return headerMetaConfigValue;
  }, [headerMetaInputValues.description, headerMetaInputValues.name, setInputValues]);

  return (
    <HeaderMetaConfigContext.Provider value={contextValue}>
      {children}
    </HeaderMetaConfigContext.Provider>
  );
}
